/**
 * Phase 20 Plan 20-02 — RecipientPhonesPanel
 *
 * Shows phones for a recipient with:
 * - Primary badge on the primary phone
 * - "Set Primary" action for non-primary phones
 * - "Remove" action with inline confirmation
 * - "Add Phone" button opens AddPhoneDialog
 */

import type { JSX } from 'react';
import { useState } from 'react';

import { AddPhoneDialog } from '@/components/notifications/AddPhoneDialog';
import { Button } from '@/components/ui/button';
import {
  useDeletePhoneMutation,
  useRecipientDetailQuery,
  useUpdatePhoneMutation,
} from '@/hooks/useNotificationsQueries';
import { toast } from '@/lib/toast';
import type { PhoneNumberDto } from '@/types/notifications';

interface RecipientPhonesPanelProps {
  readonly recipientId: number;
}

export function RecipientPhonesPanel({ recipientId }: RecipientPhonesPanelProps): JSX.Element {
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const { data, isSuccess } = useRecipientDetailQuery(recipientId);
  const updatePhone = useUpdatePhoneMutation();
  const deletePhone = useDeletePhoneMutation();

  const phones = data?.phones.filter((p) => p.isActive) ?? [];

  // Don't render the panel until the query has resolved so that the badge
  // is present in the same render as the container (test-timing guard).
  if (!isSuccess) {
    return <div className="text-xs text-muted-foreground">Loading phones…</div>;
  }

  const handleSetPrimary = (phone: PhoneNumberDto): void => {
    void updatePhone.mutateAsync({
      recipientId,
      phoneId: phone.id,
      body: { isPrimary: true },
    }).then(() => {
      toast.success('Primary phone updated');
    }).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to update phone';
      toast.error(msg);
    });
  };

  const handleDelete = (phoneId: number): void => {
    void deletePhone.mutateAsync({ recipientId, phoneId }).then(() => {
      toast.success('Phone removed');
      setConfirmDeleteId(null);
    }).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to remove phone';
      toast.error(msg);
    });
  };

  return (
    <div data-testid="phones-panel" className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Phone Numbers</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => { setAddOpen(true); }}
          data-testid="add-phone-btn"
        >
          + Add Phone
        </Button>
      </div>

      {phones.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="phones-empty">
          No phone numbers configured.
        </p>
      )}

      <ul className="space-y-1">
        {phones.map((phone) => (
          <li key={phone.id} className="flex items-center gap-2 rounded border px-2 py-1 text-sm">
            <span>
              +{phone.countryCode} {phone.phoneNumber}
            </span>
            {phone.isPrimary && (
              <span
                data-testid="phone-primary-badge"
                className="rounded bg-primary px-1 py-0.5 text-xs text-primary-foreground"
              >
                Primary
              </span>
            )}

            <div className="ml-auto flex gap-1">
              {!phone.isPrimary && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { handleSetPrimary(phone); }}
                  data-testid={`phone-set-primary-btn-${phone.id}`}
                  disabled={updatePhone.isPending}
                >
                  Set Primary
                </Button>
              )}

              {confirmDeleteId === phone.id ? (
                <div className="flex items-center gap-1" data-testid="phone-delete-confirm">
                  <span className="text-xs text-destructive">Confirm?</span>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => { handleDelete(phone.id); }}
                    disabled={deletePhone.isPending}
                    data-testid={`phone-delete-confirm-yes-${phone.id}`}
                  >
                    Yes
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setConfirmDeleteId(null); }}
                    data-testid={`phone-delete-confirm-no-${phone.id}`}
                  >
                    No
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => { setConfirmDeleteId(phone.id); }}
                  data-testid={`phone-remove-btn-${phone.id}`}
                >
                  Remove
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <AddPhoneDialog
        open={addOpen}
        recipientId={recipientId}
        onClose={() => { setAddOpen(false); }}
      />
    </div>
  );
}
